'use client';

import React from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from 'recharts';

const weeklyData = [
  { id: 'wk-mon', day: 'Mon', orders: 28, delivered: 26 },
  { id: 'wk-tue', day: 'Tue', orders: 34, delivered: 32 },
  { id: 'wk-wed', day: 'Wed', orders: 41, delivered: 34 },
  { id: 'wk-thu', day: 'Thu', orders: 38, delivered: 37 },
  { id: 'wk-fri', day: 'Fri', orders: 52, delivered: 49 },
  { id: 'wk-sat', day: 'Sat', orders: 31, delivered: 30 },
  { id: 'wk-sun', day: 'Sun', orders: 18, delivered: 18 },
];

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;
  return (
    <div className="card-elevated p-3 min-w-[140px]">
      <p className="text-xs font-600 text-muted-foreground mb-2">{label}</p>
      {payload.map((p, i) => (
        <div key={`tt-${i}`} className="flex items-center justify-between gap-4">
          <span className="text-xs text-muted-foreground capitalize">{p.name}</span>
          <span className="text-sm font-700 font-tabular" style={{ color: p.color }}>{p.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function WeeklyOrdersChart() {
  return (
    <div className="card-elevated p-5 h-full">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-700 text-foreground">Weekly Order Volume</h2>
          <p className="text-xs text-muted-foreground">Mon 28 Jul – Sun 3 Aug 2026</p>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <span className="w-2.5 h-2.5 rounded-full bg-primary inline-block" /> Orders
          </span>
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <span className="w-2.5 h-2.5 rounded-full bg-positive inline-block" /> Delivered
          </span>
        </div>
      </div>
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={weeklyData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="gradOrders" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--primary)" stopOpacity={0.25} />
                <stop offset="95%" stopColor="var(--primary)" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gradDelivered" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--positive)" stopOpacity={0.2} />
                <stop offset="95%" stopColor="var(--positive)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
            <XAxis
              dataKey="day"
              tick={{ fontSize: 11, fill: 'var(--muted-foreground)', fontFamily: 'var(--font-sans)' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fill: 'var(--muted-foreground)', fontFamily: 'var(--font-sans)' }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="orders"
              stroke="var(--primary)"
              strokeWidth={2.5}
              fill="url(#gradOrders)"
              name="orders"
            />
            <Area
              type="monotone"
              dataKey="delivered"
              stroke="var(--positive)"
              strokeWidth={2}
              fill="url(#gradDelivered)"
              name="delivered"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      {/* Summary row */}
      <div className="mt-4 grid grid-cols-3 gap-3 pt-4 border-t border-border">
        {[
          { id: 'sum-total', label: 'Total Orders', value: '242' },
          { id: 'sum-delivered', label: 'Delivered', value: '226' },
          { id: 'sum-rate', label: 'Success Rate', value: '93.4%' },
        ].map(s => (
          <div key={s.id} className="text-center">
            <p className="text-lg font-700 font-tabular text-foreground">{s.value}</p>
            <p className="text-xs text-muted-foreground">{s.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}